import { CustomAttribute } from 'types/checkout'

const CONSENT_KEY = 'openai_ads_consent'
const ATTRIBUTION_KEY = 'openai_ads_attribution'
const RETENTION_MS = 30 * 24 * 60 * 60 * 1000
export const OPENAI_CONSENT_CHANGED = 'openai-ads-consent-changed'

export function isOpenAiAdsEnabled(): boolean {
  return (
    typeof window !== 'undefined' &&
    process.env.OPENAI_ADS_ENABLED === 'true' &&
    process.env.SHOP_LOCALE === 'GB'
  )
}

// The cookie banner's notice counts as consent (option B, agreed 28 Oct 2026),
// so every UK visitor is included unless they withdraw.
export function hasOpenAiConsent(): boolean {
  if (!isOpenAiAdsEnabled()) return false
  try {
    const consent = JSON.parse(
      localStorage.getItem(CONSENT_KEY) || 'null'
    ) as Record<string, unknown> | null
    return consent?.granted !== false
  } catch {
    return true
  }
}

// For a future consent tool. Withdrawal persists until consent is granted again.
export function setOpenAiConsent(granted: boolean): void {
  if (typeof window === 'undefined') return
  try {
    if (granted) {
      localStorage.removeItem(CONSENT_KEY)
    } else {
      localStorage.setItem(CONSENT_KEY, JSON.stringify({ granted: false }))
      localStorage.removeItem(ATTRIBUTION_KEY)
    }
  } catch {
    // Unavailable storage can't record a withdrawal.
  }
  window.dispatchEvent(new Event(OPENAI_CONSENT_CHANGED))
}

export function captureOpenAiAttribution(search: string): void {
  if (typeof window === 'undefined') return
  try {
    if (!hasOpenAiConsent()) {
      localStorage.removeItem(ATTRIBUTION_KEY)
      return
    }
    const oppref = new URLSearchParams(search).get('oppref')
    if (!oppref?.trim()) return
    // Preserve the decoded opaque value exactly; don't trim/hash/normalize it.
    localStorage.setItem(
      ATTRIBUTION_KEY,
      JSON.stringify({ oppref, expiresAt: Date.now() + RETENTION_MS })
    )
  } catch {
    // Tracking must not affect browsing when storage is unavailable.
  }
}

function getOpenAiAttribution(): string | undefined {
  try {
    const attribution = JSON.parse(
      localStorage.getItem(ATTRIBUTION_KEY) || 'null'
    ) as Record<string, unknown> | null
    if (
      typeof attribution?.oppref === 'string' &&
      attribution.oppref.trim() &&
      typeof attribution.expiresAt === 'number' &&
      attribution.expiresAt > Date.now()
    ) {
      return attribution.oppref
    }
    localStorage.removeItem(ATTRIBUTION_KEY)
  } catch {
    // Missing, corrupt or inaccessible storage is equivalent to no click ID.
  }
}

export function withOpenAiCartAttributes(
  attributes: CustomAttribute[]
): CustomAttribute[] {
  const preserved = attributes.filter(
    ({ key }) => key !== '_oppref' && key !== '_openaiConsent'
  )
  if (!hasOpenAiConsent()) return preserved
  const oppref = getOpenAiAttribution()
  return [
    ...preserved,
    { key: '_openaiConsent', value: 'true' },
    ...(oppref ? [{ key: '_oppref', value: oppref }] : [])
  ]
}
